import "../../styles/animation-conversation.css";
import React, { useRef } from "react";
import mainTheme from "../../theme.tsx";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import {
  Box,
  Grid,
  IconButton,
  styled,
  ThemeProvider,
  Toolbar,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchField from "../search/SearchField.tsx";
import Conversation from "./Conversation.tsx";
import ExampleItems from "../example/ExampleItems.tsx";
import TallyLogo from "../../assets/Tally_Logotype_Offwhite_01.png";
import TallyLogoBlack from "../../assets/Tally_Logotype_Black_01.png";
import { useTypedSelector } from "../../hooks.tsx";
import ConversationPanelAnimation from "./ConversationPanelAnimation.tsx";
import ExchangePanel from "./Exchange.tsx";
import SETTINGS from "../../lib/TallyLibrary/constants/configGeneral.json";
import OpenDrawerIcon from "../icons/OpenDrawerIcon.tsx";

interface ConversationPanelProps {
  children?: React.ReactNode;
  openDrawer: boolean;
  setOpenDrawer?: React.Dispatch<React.SetStateAction<boolean>>;
}

interface ConversationPanelAnimation {
  start: () => void;
  stop: () => void;
}

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme }) => ({
  backgroundColor: "#e3e3dc",
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  variants: [
    {
      props: ({ open }) => open,
      style: {
        width: "100%",
        marginLeft: `${SETTINGS.drawerWidth}px`,
        transition: theme.transitions.create(["margin", "width"], {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
}));

const ConversationPanel: React.FC<ConversationPanelProps> = (props) => {
  const size = `min(100vw, 100vh)`;
  const theme = useTheme();

  const handleDrawerOpen = () => {
    if (props.setOpenDrawer) {
      props.setOpenDrawer(true);
    }
  };

  const exchangeIds = useTypedSelector(
    (state) => state.conversationState.exchanges.allIds,
  );

  const refAnimation = useRef<ConversationPanelAnimation>(null);

  const { anonymousSession } = useTypedSelector((state) => state.authState);

  const onPulseAnimation = () => {
    if (refAnimation.current) {
      refAnimation.current.start();
    }
  };

  const stopPulseAnimation = () => {
    if (refAnimation.current) {
      refAnimation.current.stop();
    }
  };

  const isScreenBiggerThanLg: boolean = useMediaQuery(
    theme.breakpoints.up("lg"),
  );
  const isScreenBiggerThanMd: boolean = useMediaQuery(
    theme.breakpoints.up("md"),
  );

  return (
    <ThemeProvider theme={mainTheme}>
      <Box>
        {isScreenBiggerThanLg ? (
          <Box
            sx={{
              position: "absolute",
              top: 30,
              right: 0,
            }}
          >
            <Box
              className="logo__dot"
              sx={{
                position: "absolute",
                top: "9px",
                right: "117px",
                width: "7px",
                height: "7px",
                backgroundColor: `${theme.palette.secondary.main}`,
                borderRadius: "50%",
                left: "revert",
              }}
            ></Box>
            <ConversationPanelAnimation ref={refAnimation} />
            <Box
              src={TallyLogo}
              alt="Tally logo"
              component={"img"}
              sx={[
                {
                  width: "100px",
                  position: "absolute",
                  right: "15px",
                },
              ]}
            />
          </Box>
        ) : (
          <Box />
        )}

        <Box
          sx={[
            {
              height: "100%",
              width: size,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              overflow: "hidden",
              position: "fixed",
              top: "50%",
              left: `calc(50%)`,
              backgroundColor: `${theme.palette.secondary.main}`,
              transform: "translate(-50%, -50%)",
              flexDirection: "column",
              marginBottom: "2%",
              transition: theme.transitions.create(["left", "width"], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
            },
            // css for screens greater than md-size
            isScreenBiggerThanMd &&
              props.openDrawer && {
                left: `calc(50% + ${SETTINGS.drawerWidth / 2}px)`,
                transition: theme.transitions.create(["left", "width"], {
                  easing: theme.transitions.easing.easeOut,
                  duration: theme.transitions.duration.enteringScreen,
                }),
              },
          ]}
        >
          {!isScreenBiggerThanLg ? (
            <AppBar position="fixed" open={props.openDrawer} sx={{}}>
              <Toolbar
                variant="dense"
                sx={[
                  {
                    flexDirection: "row",
                    justifyContent: "space-between",
                    p: "14px 14px",
                  },
                  props.openDrawer && {
                    justifyContent: "right",
                  },
                ]}
              >
                {!anonymousSession && !isScreenBiggerThanMd ? (
                  <IconButton
                    onClick={handleDrawerOpen}
                    sx={[
                      {
                        float: "left",
                      },
                      props.openDrawer && { display: "none" },
                    ]}
                  >
                    <OpenDrawerIcon
                      color={theme.palette.common.black}
                      fontSize={theme.typography.h5.fontSize}
                    />
                  </IconButton>
                ) : (
                  <div />
                )}
                <Box
                  sx={{
                    width: "46px",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    height: "20px",
                    margin: "8px",
                  }}
                >
                  <Box
                    sx={{
                      marginTop: "3px",
                      width: "3px",
                      height: "3px",
                      backgroundColor: `${theme.palette.common.black}`,
                      borderRadius: "50%",
                      float: "left",
                    }}
                  ></Box>
                  <Box
                    src={TallyLogoBlack}
                    alt="Tally logo"
                    component={"img"}
                    sx={[
                      {
                        width: "40px",
                        right: "0px",
                        height: "20px",
                      },
                    ]}
                  />
                </Box>
              </Toolbar>
            </AppBar>
          ) : (
            <Box />
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1, // Allow this box to grow and shrink
              width: "100%",
              justifyContent: "space-between", // This distributes the space
              marginBottom: "2%",
              marginTop: "200px",
            }}
          >
            <Conversation>
              {exchangeIds.map((exchangeId) => (
                <ExchangePanel key={exchangeId} exchangeId={exchangeId} />
              ))}
            </Conversation>
            <Grid
              sx={{
                m: { xs: "3% 0% 2% 0%" },
                width: "100%", // Ensure full width
              }}
            >
              <SearchField
                onPulseAnimation={onPulseAnimation}
                stopPulseAnimation={stopPulseAnimation}
              />
              <ExampleItems />
            </Grid>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default ConversationPanel;
